import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import DataTable, {TableColumn} from 'react-data-table-component'
import moment from 'moment'
import {Modal, Spinner} from 'react-bootstrap'
import {KTIcon} from 'src/marketConnect/helpers'
import {useAppDispatch} from 'src/redux/storeHooks'
import {
  createSuperadminNewsAsync,
  deleteSuperadminNewsAsync,
  getSuperadminNewsAsync,
  updateSuperadminNewsAsync,
} from 'src/redux/actions/superadminNewsActions'
import {NotificationType, SuperadminNewsItem} from 'src/interfaces/SuperadminNews.interface'
import useAppToast from 'src/hooks/useAppToast'
import AppDatePickerRange from '../../orders/components/AppDatePickerRange'
import TableLoadingShell from 'src/utils/TableLoadingShell'
import NewsUpdateForm, {NewsUpdateFormValues, NOTIFICATION_TYPES} from './NewsUpdateForm'
import {buildNotificationPayload} from './newsPayload'
import './superadmin-news.scss'

const emptyForm = (): NewsUpdateFormValues => ({
  releaseDate: moment().format('YYYY-MM-DD'),
  heading: '',
  description: '',
  type: 'NEWS_ANNOUNCEMENTS',
})

const formatUpdateId = (id: number) => `#N-${id}`

const formatTypeLabel = (type?: string) => {
  const match = NOTIFICATION_TYPES.find((item) => item.value === type)
  return match?.label ?? type?.replace(/_/g, ' ') ?? '—'
}

const formatReleaseDate = (item: SuperadminNewsItem) => {
  const dateValue = item.feature_released_date || item.created_at
  return moment(dateValue).format('MM/DD/YYYY')
}

const isSuccessResponse = (payload: any) =>
  payload?.status_code === 200 ||
  payload?.status_code === 201 ||
  payload?.status_code === 204 ||
  (payload?.data && payload?.status_code < 400)

const getApiErrorMessage = (payload: unknown, fallback: string) => {
  if (!payload || typeof payload !== 'object') return fallback
  const data = payload as {massage?: string; message?: string}
  return data.massage || data.message || fallback
}

const itemToFormValues = (item: SuperadminNewsItem): NewsUpdateFormValues => ({
  releaseDate: item.feature_released_date || moment(item.created_at).format('YYYY-MM-DD'),
  heading: item.title,
  description: item.description,
  type: (item.type as NotificationType) || 'NEWS_ANNOUNCEMENTS',
})

const getStatusLabel = (item: SuperadminNewsItem) => {
  const status = item.status?.toLowerCase()
  if (status === 'draft') return 'Draft'
  return 'Published'
}

const parseNewsListPayload = (payload: any): {rows: SuperadminNewsItem[]; total: number} => {
  if (!payload || payload?.status_code >= 400) {
    return {rows: [], total: 0}
  }

  const data = payload?.data ?? payload
  const results = data?.results ?? data?.items ?? (Array.isArray(data) ? data : [])

  return {
    rows: results,
    total: data?.count ?? results.length ?? 0,
  }
}

const ManageNewsUpdates: FC = () => {
  const dispatch = useAppDispatch()
  const {successToast, errorToast} = useAppToast()
  const errorToastRef = useRef(errorToast)

  useEffect(() => {
    errorToastRef.current = errorToast
  }, [errorToast])

  const [rows, setRows] = useState<SuperadminNewsItem[]>([])
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [totalRows, setTotalRows] = useState(0)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [dateRange, setDateRange] = useState<{from_date?: string; to_date?: string}>({})
  const [showDatePicker, setShowDatePicker] = useState(false)

  const [showFormModal, setShowFormModal] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [formValues, setFormValues] = useState<NewsUpdateFormValues>(emptyForm)
  const [submitting, setSubmitting] = useState(false)
  const [selectedItem, setSelectedItem] = useState<SuperadminNewsItem | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<SuperadminNewsItem | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [debouncedSearch, setDebouncedSearch] = useState('')

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search.trim())
      setCurrentPage(1)
    }, 300)

    return () => window.clearTimeout(timeoutId)
  }, [search])

  useEffect(() => {
    let cancelled = false

    const loadNews = async () => {
      setLoading(true)

      try {
        const response = await dispatch(
          getSuperadminNewsAsync({
            page: currentPage,
            limit: perPage,
            search: debouncedSearch || undefined,
            status: statusFilter || undefined,
            from_date: dateRange.from_date,
            to_date: dateRange.to_date,
          })
        )

        if (cancelled) return

        if (getSuperadminNewsAsync.rejected.match(response)) {
          errorToastRef.current('Failed to load news updates.')
          setRows([])
          setTotalRows(0)
          return
        }

        const parsed = parseNewsListPayload(response.payload)
        setRows(parsed.rows)
        setTotalRows(parsed.total)
      } catch {
        if (!cancelled) {
          errorToastRef.current('Failed to load news updates.')
          setRows([])
          setTotalRows(0)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadNews()

    return () => {
      cancelled = true
    }
  }, [currentPage, dateRange.from_date, dateRange.to_date, debouncedSearch, dispatch, perPage, statusFilter])

  const reloadNews = () => {
    setLoading(true)
    dispatch(
      getSuperadminNewsAsync({
        page: currentPage,
        limit: perPage,
        search: debouncedSearch || undefined,
        status: statusFilter || undefined,
        from_date: dateRange.from_date,
        to_date: dateRange.to_date,
      })
    )
      .then((response) => {
        if (getSuperadminNewsAsync.rejected.match(response)) {
          errorToastRef.current('Failed to load news updates.')
          setRows([])
          setTotalRows(0)
          return
        }
        const parsed = parseNewsListPayload(response.payload)
        setRows(parsed.rows)
        setTotalRows(parsed.total)
      })
      .finally(() => setLoading(false))
  }

  const handleResetFilters = () => {
    setSearch('')
    setDebouncedSearch('')
    setStatusFilter('')
    setDateRange({})
    setCurrentPage(1)
  }

  const handleOpenCreate = () => {
    setEditingId(null)
    setFormValues(emptyForm())
    setShowFormModal(true)
  }

  const handleCloseFormModal = () => {
    if (submitting) return
    setShowFormModal(false)
    setEditingId(null)
  }

  const handleOpenEdit = useCallback((row: SuperadminNewsItem) => {
    setEditingId(row.id)
    setFormValues(itemToFormValues(row))
    setShowFormModal(true)
  }, [])

  const handleSave = (event: React.FormEvent) => {
    event.preventDefault()
    setSubmitting(true)

    const payload = buildNotificationPayload(formValues)

    const onSaveComplete = (response: {payload: unknown}) => {
      if (isSuccessResponse(response.payload)) {
        successToast(editingId ? 'Update saved successfully.' : 'Update published successfully.')
        setShowFormModal(false)
        setEditingId(null)
        setFormValues(emptyForm())
        reloadNews()
        return
      }
      errorToast(
        getApiErrorMessage(
          response.payload,
          `Failed to ${editingId ? 'update' : 'publish'} notification.`
        )
      )
    }

    const savePromise = editingId
      ? dispatch(updateSuperadminNewsAsync({id: editingId, payload}))
      : dispatch(createSuperadminNewsAsync(payload))

    savePromise
      .then(onSaveComplete)
      .catch(() => errorToast(`Failed to ${editingId ? 'update' : 'publish'} notification.`))
      .finally(() => setSubmitting(false))
  }

  const handleConfirmDelete = () => {
    if (!deleteTarget) return
    setDeleting(true)

    dispatch(deleteSuperadminNewsAsync(deleteTarget.id))
      .then((response) => {
        if (isSuccessResponse(response.payload) || deleteSuperadminNewsAsync.fulfilled.match(response)) {
          successToast('Notification deleted successfully.')
          setDeleteTarget(null)
          if (selectedItem?.id === deleteTarget.id) {
            setSelectedItem(null)
          }
          reloadNews()
          return
        }
        errorToast(getApiErrorMessage(response.payload, 'Failed to delete notification.'))
      })
      .catch(() => errorToast('Failed to delete notification.'))
      .finally(() => setDeleting(false))
  }

  const columns: TableColumn<SuperadminNewsItem>[] = useMemo(
    () => [
      {
        name: 'Release Date',
        width: '140px',
        cell: (row) => formatReleaseDate(row),
      },
      {
        name: 'Type',
        width: '180px',
        cell: (row) => <span className='text-muted'>{formatTypeLabel(row.type)}</span>,
      },
      {
        name: 'Heading',
        minWidth: '220px',
        cell: (row) => <span className='fw-semibold'>{row.title}</span>,
      },
      {
        name: 'Description Summary',
        minWidth: '320px',
        cell: (row) => (
          <div className='superadmin-news-description-cell text-muted' title={row.description}>
            {row.description}
          </div>
        ),
      },
      {
        name: 'Status',
        width: '130px',
        cell: (row) => (
          <span
            className={`superadmin-news-status ${
              getStatusLabel(row) === 'Draft' ? 'is-draft' : 'is-published'
            }`}
          >
            {getStatusLabel(row)}
          </span>
        ),
      },
      {
        name: 'Action',
        width: '120px',
        center: true,
        cell: (row) => (
          <div className='d-flex justify-content-center gap-1'>
            <button
              type='button'
              className='btn btn-sm btn-icon btn-light-primary'
              title='View details'
              onClick={() => setSelectedItem(row)}
            >
              <KTIcon iconName='document' className='fs-3' />
            </button>
            <button
              type='button'
              className='btn btn-sm btn-icon btn-light-primary'
              title='Edit notification'
              onClick={() => handleOpenEdit(row)}
            >
              <KTIcon iconName='pencil' className='fs-3' />
            </button>
            <button
              type='button'
              className='btn btn-sm btn-icon btn-light-danger'
              title='Delete notification'
              onClick={() => setDeleteTarget(row)}
            >
              <KTIcon iconName='trash' className='fs-3' />
            </button>
          </div>
        ),
      },
    ],
    [handleOpenEdit]
  )

  return (
    <div className='superadmin-news-page'>
      <div className='d-flex align-items-center justify-content-between mb-5'>
        <h1 className='fs-2 fw-bold mb-0'>News & Updates Logs</h1>
      </div>

      <div className='card superadmin-news-table-card mb-0'>
        <div className='superadmin-news-controls'>
          <div className='superadmin-news-filter-item superadmin-news-filter-search'>
            <input
              type='text'
              className='form-control form-control-solid'
              placeholder='Search Headings...'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className='superadmin-news-filter-item superadmin-news-filter-status'>
            <select
              className='form-select form-select-solid'
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value)
                setCurrentPage(1)
              }}
            >
              <option value=''>All Statuses</option>
              <option value='published'>Published</option>
              <option value='draft'>Draft</option>
            </select>
          </div>

          <div className='superadmin-news-filter-item superadmin-news-filter-date'>
            <button
              type='button'
              className={`form-control form-control-solid superadmin-news-date-trigger ${
                !dateRange.from_date || !dateRange.to_date ? 'is-placeholder' : ''
              }`}
              onClick={() => setShowDatePicker(true)}
            >
              <KTIcon iconName='calendar' className='fs-5 me-2 flex-shrink-0' />
              <span className='text-truncate'>
                {dateRange.from_date && dateRange.to_date
                  ? `${moment(dateRange.from_date).format('MMM D, YYYY')} - ${moment(dateRange.to_date).format('MMM D, YYYY')}`
                  : 'Pick a Date Range'}
              </span>
            </button>
          </div>

          <div className='superadmin-news-filter-item superadmin-news-filter-reset'>
            <button type='button' className='btn btn-sm btn-primary fw-bold' onClick={handleResetFilters}>
              Reset Filters
            </button>
          </div>

          <div className='superadmin-news-filter-spacer' />

          <div className='superadmin-news-filter-item superadmin-news-filter-add'>
            <button type='button' className='btn btn-sm btn-primary fw-bold text-nowrap' onClick={handleOpenCreate}>
              <KTIcon iconName='plus' className='fs-4 me-1' />
              Add New
            </button>
          </div>

          <AppDatePickerRange
            isVisible={showDatePicker}
            handleClose={() => setShowDatePicker(false)}
            onPickRange={({startDate, endDate}) => {
              setDateRange({
                from_date: moment(startDate).format('YYYY-MM-DD'),
                to_date: moment(endDate).format('YYYY-MM-DD'),
              })
              setCurrentPage(1)
            }}
          />
        </div>

        <TableLoadingShell loading={loading}>
        <DataTable
          columns={columns}
          data={rows}
          progressPending={false}
          noDataComponent={
            <div className='py-10 text-center text-muted fw-semibold'>
              No news updates found.
            </div>
          }
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          paginationPerPage={perPage}
          onChangePage={setCurrentPage}
          onChangeRowsPerPage={setPerPage}
          responsive
          customStyles={{
            headCells: {
              style: {fontWeight: 700, fontSize: '14px', backgroundColor: '#fafafa'},
            },
            cells: {
              style: {fontSize: '14px'},
            },
          }}
        />
        </TableLoadingShell>
      </div>

      <Modal show={showFormModal} onHide={handleCloseFormModal} centered size='lg'>
        <Modal.Header closeButton>
          <Modal.Title className='fw-bold'>
            {editingId ? 'Edit News & Update' : 'Add New News & Update'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <NewsUpdateForm
            formId='modal-news-update-form'
            values={formValues}
            onChange={(field, value) => setFormValues((prev) => ({...prev, [field]: value}))}
            onSubmit={handleSave}
            submitting={submitting}
            showActions={false}
            submitLabel={editingId ? 'Update News' : 'Publish Update'}
          />
        </Modal.Body>
        <Modal.Footer className='border-0 pt-0'>
          <button type='button' className='btn btn-sm btn-light fw-semibold' onClick={handleCloseFormModal} disabled={submitting}>
            Cancel
          </button>
          <button
            type='submit'
            form='modal-news-update-form'
            className='btn btn-sm btn-primary fw-bold'
            disabled={submitting}
          >
            {submitting ? (editingId ? 'Updating...' : 'Publishing...') : editingId ? 'Update News' : 'Publish Update'}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(selectedItem)} onHide={() => setSelectedItem(null)} centered size='lg'>
        <Modal.Header closeButton>
          <Modal.Title className='fw-bold'>{selectedItem?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedItem ? (
            <>
              <div className='d-flex flex-wrap gap-4 mb-4 text-muted fs-7'>
                <span>{formatUpdateId(selectedItem.id)}</span>
                <span>{formatReleaseDate(selectedItem)}</span>
                <span>{formatTypeLabel(selectedItem.type)}</span>
                <span
                  className={`superadmin-news-status ${
                    getStatusLabel(selectedItem) === 'Draft' ? 'is-draft' : 'is-published'
                  }`}
                >
                  {getStatusLabel(selectedItem)}
                </span>
              </div>
              <p className='mb-0 fs-6 lh-lg text-gray-700'>{selectedItem.description}</p>
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer className='border-0 pt-0'>
          <button type='button' className='btn btn-sm btn-light fw-semibold' onClick={() => setSelectedItem(null)}>
            Close
          </button>
          {selectedItem ? (
            <button
              type='button'
              className='btn btn-sm btn-primary fw-bold'
              onClick={() => {
                const item = selectedItem
                setSelectedItem(null)
                handleOpenEdit(item)
              }}
            >
              Update
            </button>
          ) : null}
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(deleteTarget)} onHide={() => !deleting && setDeleteTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className='fw-bold'>Delete Notification</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className='mb-0'>
            Are you sure you want to delete{' '}
            <strong>{deleteTarget ? formatUpdateId(deleteTarget.id) : ''}</strong>? This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer className='border-0 pt-0'>
          <button
            type='button'
            className='btn btn-sm btn-light fw-semibold'
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
          >
            Cancel
          </button>
          <button type='button' className='btn btn-sm btn-danger fw-bold' onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default ManageNewsUpdates
